import clsx from 'clsx';
import { Label } from '../Label';
import { ChangeEvent } from 'react';

interface Props extends BaseProps {
  color?: ColorVariant;
  direction?: FlexDirection;
  disabled?: boolean;
  label: string;
  onChange?(event: ChangeEvent<HTMLInputElement>): any;
  position?: FlexPosition;
  required?: boolean;
  selection: string | number;
  size?: SizeVariant;
}

export const RadioButton = (props: Props) => {
  const {
    className,
    color = 'primary',
    direction = 'row',
    disabled = false,
    label,
    onChange,
    position = 'end',
    required = false,
    selection,
    size = 'medium',
    value,
    ...rest
  } = props;
  const formattedLabel = label.toLowerCase().trim().replaceAll(' ', '-');
  const isChecked = selection === value;
  const classes = useStyles();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChange !== undefined) onChange(event);
  };

  return (
    <Label
      id={`label-${formattedLabel}`}
      classNames={{ label: disabled ? classes.disabled : undefined }}
      direction={direction}
      required={required}
      text={label}
      opacity="full"
      position={position}
    >
      <span className="relative">
        <input
          id={`${formattedLabel}-radio`}
          aria-labelledby={`label-${formattedLabel}`}
          aria-required={required}
          aria-disabled={disabled}
          aria-checked={isChecked}
          className={clsx(classes.input.default, {
            [classes.disabled]: disabled,
          })}
          checked={isChecked}
          onChange={handleChange}
          required={required}
          tabIndex={0}
          type="radio"
          value={value}
          {...rest}
        />
        <div
          className={clsx(classes.input.radio, classes.input.size[size], {
            [classes.input.selected[color]]: isChecked,
            [classes.input.disabled]: disabled,
          })}
        >
          <div
            className={clsx(
              classes.input.radioDot.default,
              classes.input.radioDot.color[color],
              classes.input.radioDot.size[size],
              {
                [classes.input.show]: isChecked,
                [classes.input.radioDot.disabled]: disabled,
              }
            )}
          />
        </div>
      </span>
    </Label>
  );
};

const useStyles = () => ({
  disabled: '!cursor-default opacity-70 pointer-events-none',
  icon: 'top-0 left-0 pointer-events-none',
  input: {
    default: 'absolute cursor-pointer opacity-0',
    disabled:
      '!cursor-default !pointer-events-none !border-border-light dark:!border-border-dark',
    radio:
      'box-content border rounded-full pointer-events-none border-border-light dark:border-border-dark',
    radioDot: {
      default: 'hidden absolute rounded-full top-0 left-0 pointer-events-none',
      disabled:
        '!cursor-default !pointer-events-none !bg-border-light dark:!bg-border-dark',
      size: {
        small: 'w-[7px] h-[7px] top-[3px] left-[3px]',
        medium: 'w-[10px] h-[10px] top-[4px] left-[4px]',
        large: 'w-[13px] h-[13px] top-[3px] left-[3px]',
      },
      color: {
        primary: 'bg-primary',
        secondary: 'bg-secondary',
      },
    },
    size: {
      small: 'w-[13px] h-[13px]',
      medium: 'w-[16px] h-[16px]',
      large: 'w-[19px] h-[19px]',
    },
    show: '!block',
    selected: {
      primary: '!border-primary',
      secondary: '!border-secondary',
    },
  },
  container: {
    default: 'flex gap',
    row: 'flex-row',
    column: 'flex-col',
  },
});
